import React from "react";
import { Group, Stack, Text } from "@mantine/core";
import { CommissionRecord } from "../../../types/commission";
import { formatTierSummary } from "../../../utils/commissionUtils";
import { SettingsEditButton, SettingsField } from "../common";
import {
  MethodBadge,
  bucketDisplay,
  formatEffectiveDate,
  isServiceSpecific,
} from "./commissionDisplay";
import ServiceCommissionsMenu from "./ServiceCommissionsMenu";

const valueText = (value: string | null): React.ReactNode =>
  value ?? (
    <Text component="span" c="dimmed" fw={400} size="sm">
      Not set
    </Text>
  );

interface CurrentRateDetailsProps {
  commission: CommissionRecord;
  staffId: string;
  /** Edit this rate; omitted without the edit permission. */
  onEdit?: () => void;
}

/** Every detail of the commission record currently in force, with the full
 * service menu when it pays service-specific rates. */
const CurrentRateDetails: React.FC<CurrentRateDetailsProps> = ({
  commission,
  staffId,
  onEdit,
}) => {
  const serviceSpecific = isServiceSpecific(commission);
  return (
    <Stack gap="xs">
      {onEdit && (
        <Group justify="flex-end">
          <SettingsEditButton variant="outline" onClick={onEdit} />
        </Group>
      )}
      <Stack gap={0}>
        <SettingsField label="Method">
          <MethodBadge commission={commission} />
        </SettingsField>
        <SettingsField label="Effective since">
          {formatEffectiveDate(commission.effective_start_date)}
        </SettingsField>
        {commission.calculation_method === "NUM_SERVICES" && (
          <SettingsField label="Threshold">
            {valueText(
              commission.num_services_cutoff != null
                ? `More than ${commission.num_services_cutoff} services a day`
                : null,
            )}
          </SettingsField>
        )}
        {commission.calculation_method === "TIERED" && (
          <SettingsField label="Tier schedule">
            <Text component="span" size="sm" style={{ whiteSpace: "pre-line" }}>
              {formatTierSummary(commission.tiers)}
            </Text>
          </SettingsField>
        )}
        <SettingsField label="Product commission">
          {valueText(bucketDisplay(commission, "products"))}
        </SettingsField>
        <SettingsField
          label="Service commission"
          withDivider={!serviceSpecific}
        >
          {serviceSpecific
            ? "Service-specific"
            : valueText(bucketDisplay(commission, "services"))}
        </SettingsField>
      </Stack>
      {serviceSpecific && (
        <ServiceCommissionsMenu record={commission} staffId={staffId} />
      )}
    </Stack>
  );
};

export default CurrentRateDetails;
